import { useQueryClient } from '@tanstack/react-query';
import { useTreatmentActions } from './useTreatmentActions';
import React, { useCallback, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Avatar } from '../../../components/Avatar';
import CopiableText from '../../../components/CopiableText';
import { ErrorBoundary } from '../../../components/ErrorBoundary';
import Loading from '../../../components/Loading';
import { ChatPanel } from '../../../components/patient/ChatPanel';
import { IconsRow } from '../../../components/patient/IconsRow';
import { PatientFilesTab } from '../../../components/patient/PatientFilesTab';
import { PatientActionsTab } from '../../../components/patient/actions/PatientActionsTab';
import { PatientNotesTab } from '../../../components/patient/notes/PatientNotesTab';
import { OverviewTab } from '../../../components/patient/overview/OverviewTab';
import { MonitoringTabBadge, useChatApiVersion } from '@odo/shared-ui';
import { dmScansQueryConfig } from '../../../services/patient/dmScans';
import { ScansSection } from '../../../components/patient/monitoring/ScansSection';
import { usePatientActions } from '../../../contexts/PatientActionsContext';
import { useRecentActions } from '../../../contexts/RecentActionsContext';
import { usePatientAssets } from '../../../hooks/usePatientAssets';
import { useOverviewPatient } from '../../../hooks/useOverviewPatient';
import { usePatientNotes } from '../../../hooks/usePatientNotes';
import { useOverviewDocAssets } from '../../../hooks/useOverviewDocAssets';
import { reportError } from '../../../services/errorReporting';
import { usePatientMetadata } from '../../../providers/PatientMetadataProvider';
import { useToast } from '../../../providers/ToastProvider';
import { PatientChatProvider } from '@odo/shared-ui';
import { getChatService } from '../../../services/chatService';
import {
    ArrowTopRightOnSquareIcon,
    ChatBubbleOvalLeftIcon,
    ChevronRightIcon,
    HomeIcon,
    PaperClipIcon
} from '@heroicons/react/24/outline';
import invisalignLogo from '../../../assets/icons/align-logo.svg';
import dmIcon from '../../../assets/icons/dm.svg';
import sparkLogo from '../../../assets/icons/spark-logo.svg';
import EndTreatmentModal from '../../../components/modals/EndTreatmentModal';
import ReactivateCaseModal from '../../../components/modals/ReactivateCaseModal';
import TriggerRefinementModal from '../../../components/modals/TriggerRefinementModal';
import { StartMonitoringPatient, type OverviewPatient } from '../../../services/patient';
import {
    MonitoringService,
    type MonitoringServiceResult
} from '../../../services/patient/monitoring';
import type {
    MonitoringAction,
    MonitoringActionOptions
} from '../../../components/patient/actions/monitoringStatus';
import { CHAT_QUERY_PARAM, CHAT_QUERY_OPEN } from '../../../utils/constants';
import { formatPhone } from '../../../utils/contact';
import { estimateAge, formatDate } from '../../../utils/time';

const VALID_TABS = ['overview', 'monitoring', 'files', 'notes', 'chat', 'actions'] as const;
type OverviewPageTab = (typeof VALID_TABS)[number];
const DEFAULT_TAB: OverviewPageTab = 'overview';

function isOverviewTab(value: string | null): value is OverviewPageTab {
    return !!value && (VALID_TABS as readonly string[]).includes(value);
}

// `?tab=` is the single source of truth for the active tab. The legacy
// `?chat=open` deep link (still sent by ChatCell's fallback chat
// button) only applies when `tab` is absent, so an explicit `?tab=...`
// always wins over it.
function readActiveTab(searchParams: URLSearchParams): OverviewPageTab {
    const rawTab = searchParams.get('tab');
    if (isOverviewTab(rawTab)) return rawTab;
    if (searchParams.get(CHAT_QUERY_PARAM) === CHAT_QUERY_OPEN) return 'chat';
    return DEFAULT_TAB;
}

/**
 * Slim breadcrumb header mirroring the approval flow's PatientActionPage
 * Breadcrumb: Home -> Patients -> {patient name} -> Overview. The avatar is
 * intentionally omitted here — SidebarLayout already overlays one top-right.
 */
const OverviewBreadcrumb: React.FC<{ patientName: string }> = ({ patientName }) => (
    <div className="h-[88px] flex items-center pl-6 pr-20 flex-shrink-0">
        <nav className="flex items-center gap-1 text-xs text-gray-500" aria-label="Breadcrumb">
            <Link to="/home" className="hover:text-gray-800 transition-colors flex items-center">
                <HomeIcon className="w-3.5 h-3.5" />
            </Link>
            <ChevronRightIcon className="w-3 h-3 text-gray-300" />
            <Link to="/home" className="hover:text-gray-800 transition-colors">
                Patients
            </Link>
            <ChevronRightIcon className="w-3 h-3 text-gray-300" />
            <span className="text-gray-700 font-semibold truncate max-w-[200px]">
                {patientName || 'Patient'}
            </span>
            <ChevronRightIcon className="w-3 h-3 text-gray-300" />
            <span className="text-gray-900 font-semibold">Overview</span>
        </nav>
    </div>
);

const FoldableSection: React.FC<{
    title: string;
    defaultOpen?: boolean;
    children: React.ReactNode;
}> = ({ title, defaultOpen = true, children }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="w-full">
            <button
                type="button"
                className="w-full flex items-center justify-between py-2"
                onClick={() => setOpen(!open)}
            >
                <span className="text-sm font-bold text-gray-800">{title}</span>
                <svg
                    className={`w-4 h-4 text-gray-600 transition-transform ${open ? 'transform rotate-180' : ''}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                >
                    <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 10.17l3.71-2.94a.75.75 0 111.06 1.06l-4.24 3.36a.75.75 0 01-.94 0L5.21 8.29a.75.75 0 01.02-1.08z"
                        clipRule="evenodd"
                    />
                </svg>
            </button>
            {open && <div className="mt-2">{children}</div>}
        </div>
    );
};

function safeOpenUrl(value: string | undefined | null): void {
    if (!value) return;
    try {
        const url = new URL(value);
        if (url.protocol === 'https:' || url.protocol === 'http:') {
            // `noopener` is what matters: these are third-party manufacturer and
            // DM portals, and without it the opened page keeps a live
            // `window.opener` handle and can navigate this tab elsewhere
            // (reverse tabnabbing). `noreferrer` additionally withholds the
            // referrer, which these portals have no need for.
            window.open(value, '_blank', 'noopener,noreferrer');
        }
    } catch {
        // invalid URL — do nothing
    }
}

/** Resolves the manufacturer wordmark for the portal button. Returns null for
 *  unknown manufacturers, in which case the text label renders instead. */
function manufacturerLogo(manufacturer: string | null | undefined): string | null {
    const name = (manufacturer ?? '').toLowerCase();
    if (name.includes('spark')) return sparkLogo;
    if (name.includes('invisalign') || name.includes('align')) return invisalignLogo;
    return null;
}

/** Compact external-portal link button — two of these share one row on the
 *  patient card (manufacturer portal + Dental Monitoring). Shows a brand
 *  logo when one is available; disabled with a tooltip when the patient has
 *  no link for that portal. */
const PortalLinkButton = ({
    label,
    url,
    logo,
    logoStyle = 'wordmark'
}: {
    label: string;
    url: string | null | undefined;
    /** Brand asset. 'wordmark' logos replace the text label; 'icon' logos sit beside it. */
    logo?: string | null;
    logoStyle?: 'wordmark' | 'icon';
}) => {
    const available = !!url;
    return (
        <button
            type="button"
            onClick={() => safeOpenUrl(url)}
            disabled={!available}
            title={available ? `Open ${label}` : `No ${label} link for this patient`}
            className={`flex-1 min-w-0 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${
                available
                    ? 'border-gray-300 text-gray-800 bg-white hover:bg-gray-50'
                    : 'border-gray-200 text-gray-400 bg-white cursor-not-allowed opacity-60 grayscale'
            }`}
        >
            {logo && logoStyle === 'wordmark' ? (
                <img src={logo} alt={label} className="h-4 w-auto max-w-full object-contain" />
            ) : (
                <>
                    {logo && (
                        <img src={logo} alt="" aria-hidden="true" className="h-4 w-4 shrink-0" />
                    )}
                    <span className="truncate">{label}</span>
                </>
            )}
            <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 shrink-0" />
        </button>
    );
};

const PatientCard = ({ patient }: { patient: OverviewPatient | null }) => {
    const { getProfilePictureUrl, getProfileThumbnailUrl } = usePatientMetadata();

    const resolvedDuration = patient?.treatmentDuration
        ? '~'.concat(patient.treatmentDuration).concat(' months')
        : 'N/A';
    const orthopulseVal = patient?.orthopulseStatus?.toLowerCase();
    const resolvedOrthopulseStatus = orthopulseVal?.includes('yes')
        ? 'Yes'
        : orthopulseVal?.includes('no')
          ? 'No'
          : 'N/A';

    const profilePictureUrl = getProfilePictureUrl(patient?.id);
    const profileThumbnailUrl = getProfileThumbnailUrl(patient?.id);

    return (
        <div className="w-full h-full bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-col items-stretch gap-3 min-h-0 overflow-hidden">
            {/* Patient name */}
            <h3 className="text-base font-bold text-gray-800 text-center shrink-0">
                {patient?.name || 'N/A'}
            </h3>

            {/* Patient picture — the avatar is the single biggest non-shrinking
                cost in this column, so it steps down on short viewports to keep
                room for the scrolling detail sections below. */}
            <div className="w-full flex justify-center shrink-0">
                <div className="w-full max-w-[180px] [@media(max-height:900px)]:max-w-[128px] [@media(max-height:760px)]:max-w-[96px] aspect-square">
                    <Avatar
                        user={{ name: patient?.name ?? 'N/A', email: patient?.email ?? 'N/A' }}
                        imageURL={profilePictureUrl ?? ''}
                        thumbnailURL={profileThumbnailUrl ?? undefined}
                    />
                </div>
            </div>

            {/* External portal links — manufacturer (Spark/Invisalign) + Dental Monitoring */}
            <div className="w-full flex gap-2 shrink-0">
                <PortalLinkButton
                    label={patient?.manufacturer || 'Treatment plan'}
                    url={patient?.manufacturerLink}
                    logo={manufacturerLogo(patient?.manufacturer)}
                />
                <PortalLinkButton label="DM" url={patient?.dmLink} logo={dmIcon} logoStyle="icon" />
            </div>

            {/* Separation line */}
            <div className="w-full h-px bg-gray-200 shrink-0" aria-hidden="true"></div>

            {/* Only the sections below the links scroll — name/photo/links stay pinned */}
            <div className="flex-1 min-h-0 overflow-y-auto scrollbar-subtle flex flex-col gap-3">
                {/* Treatment overview (foldable) */}
                <FoldableSection title="Treatment overview">
                    <div className="flex flex-col gap-3">
                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-semibold text-[#6B7280]">Duration</span>
                            <span className="text-gray-800">{resolvedDuration}</span>
                        </div>
                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-semibold text-[#6B7280]">Case type</span>
                            <span className="text-gray-800">
                                {(patient?.caseType ?? 'N/A') +
                                    ' - ' +
                                    (patient?.subCaseType ?? 'N/A')}
                            </span>
                        </div>
                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-semibold text-[#6B7280]">Orthopulse</span>
                            <span className="text-gray-800">{resolvedOrthopulseStatus}</span>
                        </div>
                    </div>
                </FoldableSection>

                {/* Monitoring overview (foldable) */}
                <FoldableSection title="Monitoring overview">
                    <div className="flex flex-col gap-3">
                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-semibold text-[#6B7280]">DM app</span>
                            <span className="text-gray-800">
                                {patient?.dmAppActivationStatus ?? 'N/A'}
                            </span>
                        </div>
                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-semibold text-[#6B7280]">
                                Monitoring status
                            </span>
                            <span className="text-gray-800">
                                {patient?.monitoringStatus ?? 'N/A'}
                            </span>
                        </div>
                    </div>
                </FoldableSection>

                {/* Details (foldable) */}
                <FoldableSection title="Details">
                    <div className="w-full flex flex-col gap-3">
                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-semibold text-[#6B7280]">
                                Date of birth
                            </span>
                            <span className="text-gray-800">
                                {patient?.dateOfBirth
                                    ? formatDate(patient.dateOfBirth) +
                                      ' ' +
                                      estimateAge(patient.dateOfBirth)
                                    : 'N/A'}
                            </span>
                        </div>
                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-semibold text-[#6B7280]">Email</span>
                            <CopiableText value={patient?.email ?? ''} />
                        </div>
                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-semibold text-[#6B7280]">Phone</span>
                            <span className="text-gray-800">
                                {patient?.phone ? formatPhone(patient.phone) : 'N/A'}
                            </span>
                        </div>
                    </div>
                </FoldableSection>
            </div>
        </div>
    );
};

/**
 * Dispatches a start/pause/resume/stop monitoring call. 'start' requires
 * `options.quickstart` and 'pause' requires `options.resumeDate` — both
 * narrowed here (rather than at the call site) so TypeScript can confirm
 * they're present without a non-null assertion. Both guards are defensive:
 * the real validation happens in `handleMonitoringAction` before this runs,
 * mirroring the callers (ManageMonitoringModal's start form / one-click
 * pause row).
 */
async function runMonitoringAction(
    patientId: string,
    action: MonitoringAction,
    options?: MonitoringActionOptions
): Promise<MonitoringServiceResult> {
    if (action === 'start') {
        if (!options?.quickstart) {
            throw new Error('A quickstart is required to start monitoring');
        }
        return MonitoringService.startDMMonitoring(patientId, options.quickstart);
    }
    if (action === 'pause') {
        if (options?.resumeDate === undefined) {
            throw new Error('Resume date is required to pause monitoring');
        }
        return MonitoringService.pauseDMMonitoring(patientId, options.resumeDate);
    }
    if (action === 'resume') {
        return MonitoringService.resumeDMMonitoring(patientId);
    }
    return MonitoringService.stopDMMonitoring(patientId);
}

const PatientOverview: React.FC = () => {
    // Remounting on the version is not cosmetic: v1 ids are Monday update ids
    // and v2 ids are UUIDs, so the provider must rebuild rather than reconcile
    // its cached messages onto ids from the other world.
    const { version: chatApiVersion } = useChatApiVersion();
    const actions = usePatientActions();
    const queryClient = useQueryClient();
    const { data: patient, isLoading } = useOverviewPatient();
    const [searchParams, setSearchParams] = useSearchParams();
    const { showToast } = useToast();
    const { hasUnreadMessages } = usePatientMetadata();
    const { markActionCompleted } = useRecentActions();

    const patientId = patient?.id ?? null;
    const chatUnread = hasUnreadMessages(patientId);
    // Identity for ManageMonitoringModal's 'start' step form — no `monitoring`
    // block, so the form's paused-resume variant never activates there (start
    // is only offered by getAvailableActions for not-started/stopped anyway).
    const monitoringPatient = useMemo<StartMonitoringPatient | null>(
        () => (patient ? { id: patient.id, name: patient.name, email: patient.email } : null),
        [patient]
    );

    const activeTab = readActiveTab(searchParams);
    const handleTabChange = useCallback(
        (tab: OverviewPageTab) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    if (tab === DEFAULT_TAB) {
                        next.delete('tab');
                    } else {
                        next.set('tab', tab);
                    }
                    // Once the user interacts with a tab directly, drop the
                    // one-time chat=open deep link — otherwise, since the
                    // default tab omits `tab`, navigating back to Monitoring
                    // and refreshing would fall through to the stale
                    // chat=open param and re-open Message ODO instead.
                    next.delete(CHAT_QUERY_PARAM);
                    return next;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );
    const {
        assets,
        isLoading: isAssetsLoading,
        fetchError: assetsFetchError,
        refreshAssets
    } = usePatientAssets(patientId, { skip: activeTab !== 'files' });
    // Eager notes fetch (admin-dashboard pattern): powers the count pill on the
    // Notes tab label. Shares the query key with PatientNotesTab's instance, so
    // opening the tab reuses this fetch rather than issuing a second one.
    const { notes: allNotes } = usePatientNotes(patientId ?? undefined, {
        enabled: !!patientId
    });
    const [isMonitoringActionLoading, setIsMonitoringActionLoading] = useState(false);
    const [isSendLinkLoading, setIsSendLinkLoading] = useState(false);

    // GCS versions + Monday files combined per doc type
    const { dxReportDocs, sdiDocs, rdiDocs } = useOverviewDocAssets(patientId);

    // Returns whether the action succeeded so ManageMonitoringModal can close
    // itself on success and stay open (back on the menu) on failure.
    const handleMonitoringAction = useCallback(
        async (action: MonitoringAction, options?: MonitoringActionOptions): Promise<boolean> => {
            if (!patientId || isMonitoringActionLoading) return false;

            // Both guards are defensive: the real UI enforces these before
            // calling here (ManageMonitoringModal's start form always supplies
            // a quickstart, and its one-click pause row always supplies a
            // resumeDate) — this protects against a caller regression, not an
            // expected path.
            if (action === 'start' && !options?.quickstart) {
                showToast('A treatment phase is required to start monitoring', 'error');
                return false;
            }
            if (action === 'pause' && options?.resumeDate === undefined) {
                showToast('A resume date is required to pause monitoring', 'error');
                return false;
            }

            setIsMonitoringActionLoading(true);
            try {
                const result = await runMonitoringAction(patientId, action, options);

                if (result.success) {
                    showToast(result.message || `Monitoring ${action}ed successfully`, 'success');
                    await queryClient.invalidateQueries({ queryKey: ['patient', patientId] });
                    return true;
                }
                showToast(result.error || `Failed to ${action} monitoring`, 'error');
                return false;
            } finally {
                setIsMonitoringActionLoading(false);
            }
        },
        [patientId, queryClient, showToast, isMonitoringActionLoading]
    );

    const handleSendDMActivationLink = useCallback(async () => {
        if (!patientId || isSendLinkLoading) return;

        setIsSendLinkLoading(true);
        try {
            const result = await MonitoringService.sendDMActivationLink(patientId);

            if (result.success) {
                showToast(result.message || 'DM activation link sent successfully', 'success');
            } else {
                showToast(result.error || 'Failed to send DM activation link', 'error');
            }
        } finally {
            setIsSendLinkLoading(false);
        }
    }, [patientId, showToast, isSendLinkLoading]);

    const {
        handleEndTreatment,
        isEndTreatmentLoading,
        isEndTreatmentModalOpen,
        setIsEndTreatmentModalOpen,
        handleTriggerRefinement,
        isTriggerRefinementLoading,
        isTriggerRefinementModalOpen,
        setIsTriggerRefinementModalOpen,
        handleReactivateCase,
        isReactivateCaseLoading,
        isReactivateCaseModalOpen,
        setIsReactivateCaseModalOpen
    } = useTreatmentActions({
        patientId,
        actionRequired: patient?.actionRequired,
        showToast,
        markActionCompleted,
        actions
    });

    const pageContent = (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            <OverviewBreadcrumb patientName={patient?.name ?? ''} />

            <div className="flex-1 min-h-0 flex gap-4 px-6 pb-5 overflow-hidden">
                {/* Left: Patient Card (fixed width) */}
                {/* Patient card column: ~22% of the row, clamped so it neither
                    crushes the card on small screens (200px floor) nor balloons
                    on wide ones (280px cap) — the tabs card takes the rest. */}
                <div className="flex-none w-[22%] min-w-[200px] max-w-[280px] min-h-0">
                    <PatientCard patient={patient ?? null} />
                </div>

                {/* Right: Tabbed content — single white card, tab bar as the card header */}
                <div className="flex-1 min-w-0 min-h-0 bg-white border border-gray-200 rounded-xl shadow-sm flex flex-col overflow-hidden">
                    {/* Tab bar + icon row, merged on one line */}
                    <div className="flex items-center justify-between gap-4 px-5 border-b border-gray-200 flex-shrink-0">
                        <div
                            role="tablist"
                            aria-label="Patient overview sections"
                            className="flex items-center gap-6 min-w-0 overflow-x-auto scrollbar-hide"
                        >
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeTab === 'overview'}
                                onClick={() => handleTabChange('overview')}
                                className={`py-3 text-sm whitespace-nowrap transition-colors border-b-2 -mb-px ${
                                    activeTab === 'overview'
                                        ? 'text-gray-700 font-bold border-yellow-400'
                                        : 'text-gray-400 font-normal border-transparent hover:text-gray-500'
                                }`}
                            >
                                Overview
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeTab === 'monitoring'}
                                onClick={() => handleTabChange('monitoring')}
                                className={`py-3 text-sm whitespace-nowrap transition-colors border-b-2 -mb-px ${
                                    activeTab === 'monitoring'
                                        ? 'text-gray-700 font-bold border-yellow-400'
                                        : 'text-gray-400 font-normal border-transparent hover:text-gray-500'
                                }`}
                            >
                                <span className="inline-flex items-center gap-1.5">
                                    Monitoring
                                    <MonitoringTabBadge
                                        queries={dmScansQueryConfig}
                                        patientId={patientId}
                                        dmId={patient?.dmId}
                                    />
                                </span>
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeTab === 'files'}
                                onClick={() => handleTabChange('files')}
                                className={`flex items-center gap-1.5 py-3 text-sm whitespace-nowrap transition-colors border-b-2 -mb-px ${
                                    activeTab === 'files'
                                        ? 'text-gray-700 font-bold border-yellow-400'
                                        : 'text-gray-400 font-normal border-transparent hover:text-gray-500'
                                }`}
                            >
                                Files
                                <PaperClipIcon className="w-4 h-4" />
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeTab === 'notes'}
                                onClick={() => handleTabChange('notes')}
                                className={`py-3 text-sm whitespace-nowrap transition-colors border-b-2 -mb-px flex items-center gap-1.5 ${
                                    activeTab === 'notes'
                                        ? 'text-gray-700 font-bold border-yellow-400'
                                        : 'text-gray-400 font-normal border-transparent hover:text-gray-500'
                                }`}
                            >
                                Notes
                                {allNotes.length > 0 && (
                                    <span
                                        className={`text-[11px] font-medium px-1.5 py-0.5 rounded-full ${
                                            activeTab === 'notes'
                                                ? 'bg-gray-900 text-white'
                                                : 'bg-gray-100 text-gray-500'
                                        }`}
                                    >
                                        {allNotes.length}
                                    </span>
                                )}
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeTab === 'chat'}
                                onClick={() => handleTabChange('chat')}
                                className={`flex items-center gap-1.5 py-3 text-sm whitespace-nowrap transition-colors border-b-2 -mb-px ${
                                    activeTab === 'chat'
                                        ? 'text-gray-700 font-bold border-yellow-400'
                                        : 'text-gray-400 font-normal border-transparent hover:text-gray-500'
                                }`}
                            >
                                Message ODO
                                {/* Inline between the label and the icon rather than
                                    floating off the tab's corner: absolutely positioned
                                    it sat outside the tab's own bounds, reading as if it
                                    belonged to the tab bar rather than to this tab. */}
                                {chatUnread && (
                                    <span
                                        aria-label="Unread messages"
                                        className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0"
                                    />
                                )}
                                <ChatBubbleOvalLeftIcon className="w-4 h-4" />
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeTab === 'actions'}
                                onClick={() => handleTabChange('actions')}
                                className={`py-3 text-sm whitespace-nowrap transition-colors border-b-2 -mb-px ${
                                    activeTab === 'actions'
                                        ? 'text-gray-700 font-bold border-yellow-400'
                                        : 'text-gray-400 font-normal border-transparent hover:text-gray-500'
                                }`}
                            >
                                Actions
                            </button>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                            <ErrorBoundary
                                fallback={
                                    <div className="text-red-600 text-sm">
                                        Unable to load actions
                                    </div>
                                }
                                onError={(error) => {
                                    reportError({
                                        source: 'dashboard',
                                        message: `IconsRow error: ${error.message}`,
                                        stack: error.stack,
                                        url: window.location.href,
                                        pathname: window.location.pathname,
                                        userAgent: navigator.userAgent,
                                        errorType: 'react_boundary'
                                    });
                                    showToast('Failed to load monitoring actions', 'error');
                                }}
                            >
                                <IconsRow
                                    dxReportDocs={dxReportDocs}
                                    sdiDocs={sdiDocs}
                                    rdiDocs={rdiDocs}
                                />
                            </ErrorBoundary>
                        </div>
                    </div>

                    {/* Panel body */}
                    <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
                        {/* Overview tab (default landing view) */}
                        {activeTab === 'overview' && (
                            <ErrorBoundary
                                fallback={
                                    <div className="flex items-center justify-center h-full text-red-600 text-sm">
                                        Unable to load overview
                                    </div>
                                }
                                onError={(error) => {
                                    reportError({
                                        source: 'dashboard',
                                        message: `OverviewTab error: ${error.message}`,
                                        stack: error.stack,
                                        url: window.location.href,
                                        pathname: window.location.pathname,
                                        userAgent: navigator.userAgent,
                                        errorType: 'react_boundary'
                                    });
                                    showToast('Failed to load overview', 'error');
                                }}
                            >
                                <OverviewTab patient={patient ?? null} patientId={patientId} />
                            </ErrorBoundary>
                        )}

                        {/* Files tab */}
                        {activeTab === 'files' && patientId && (
                            <PatientFilesTab
                                patientId={patientId}
                                patientName={patient?.name}
                                assets={assets}
                                isLoading={isAssetsLoading}
                                error={assetsFetchError}
                                refreshAssets={refreshAssets}
                                hideCardChrome
                            />
                        )}

                        {/* Notes tab */}
                        {activeTab === 'notes' && patientId && (
                            <ErrorBoundary
                                fallback={
                                    <div className="flex items-center justify-center h-full text-red-600 text-sm">
                                        Unable to load notes
                                    </div>
                                }
                                onError={(error) => {
                                    reportError({
                                        source: 'dashboard',
                                        message: `PatientNotesTab error: ${error.message}`,
                                        stack: error.stack,
                                        url: window.location.href,
                                        pathname: window.location.pathname,
                                        userAgent: navigator.userAgent,
                                        errorType: 'react_boundary'
                                    });
                                    showToast('Failed to load notes', 'error');
                                }}
                            >
                                <PatientNotesTab
                                    patientId={patientId}
                                    isActive={activeTab === 'notes'}
                                />
                            </ErrorBoundary>
                        )}

                        {/* Monitoring tab */}
                        {activeTab === 'monitoring' && (
                            <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                                {/* scrollable body */}
                                <div className="flex-1 min-h-0 p-5 flex flex-col overflow-hidden">
                                    <ScansSection
                                        patientId={patientId}
                                        dmId={patient?.dmId}
                                        totalAligners={patient?.totalAligners ?? null}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Message ODO tab */}
                        {activeTab === 'chat' && patientId && (
                            <ChatPanel className="flex-1 min-h-0 overflow-hidden flex flex-col" />
                        )}

                        {/* Actions tab */}
                        {activeTab === 'actions' && patientId && (
                            <ErrorBoundary
                                fallback={
                                    <div className="flex items-center justify-center h-full text-red-600 text-sm">
                                        Unable to load actions
                                    </div>
                                }
                                onError={(error) => {
                                    reportError({
                                        source: 'dashboard',
                                        message: `PatientActionsTab error: ${error.message}`,
                                        stack: error.stack,
                                        url: window.location.href,
                                        pathname: window.location.pathname,
                                        userAgent: navigator.userAgent,
                                        errorType: 'react_boundary'
                                    });
                                    showToast('Failed to load actions', 'error');
                                }}
                            >
                                <PatientActionsTab
                                    monitoringStatus={patient?.monitoringStatus}
                                    dmAppActivationStatus={patient?.dmAppActivationStatus}
                                    dmId={patient?.dmId}
                                    internalCaseStatus={patient?.internalCaseStatus}
                                    version={patient?.version}
                                    monitoringPatient={monitoringPatient}
                                    onMonitoringAction={handleMonitoringAction}
                                    onSendDMActivationLink={handleSendDMActivationLink}
                                    isMonitoringActionLoading={isMonitoringActionLoading}
                                    isSendLinkLoading={isSendLinkLoading}
                                    onEndTreatment={() => setIsEndTreatmentModalOpen(true)}
                                    isEndTreatmentLoading={isEndTreatmentLoading}
                                    onTriggerRefinement={() =>
                                        setIsTriggerRefinementModalOpen(true)
                                    }
                                    isTriggerRefinementLoading={isTriggerRefinementLoading}
                                    onReactivateCase={() => setIsReactivateCaseModalOpen(true)}
                                    isReactivateCaseLoading={isReactivateCaseLoading}
                                />
                            </ErrorBoundary>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <>
            {isLoading ? (
                <Loading />
            ) : patientId ? (
                <PatientChatProvider
                    patientId={patientId}
                    key={chatApiVersion}
                    service={getChatService(chatApiVersion)}
                >
                    {pageContent}
                </PatientChatProvider>
            ) : (
                pageContent
            )}
            <EndTreatmentModal
                isOpen={isEndTreatmentModalOpen}
                onClose={() => setIsEndTreatmentModalOpen(false)}
                onSubmit={handleEndTreatment}
                isSubmitting={isEndTreatmentLoading}
            />
            <TriggerRefinementModal
                isOpen={isTriggerRefinementModalOpen}
                onClose={() => setIsTriggerRefinementModalOpen(false)}
                onSubmit={handleTriggerRefinement}
                isSubmitting={isTriggerRefinementLoading}
            />
            <ReactivateCaseModal
                isOpen={isReactivateCaseModalOpen}
                onClose={() => setIsReactivateCaseModalOpen(false)}
                onSubmit={handleReactivateCase}
                isSubmitting={isReactivateCaseLoading}
            />
        </>
    );
};

export default PatientOverview;
